if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;

      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;

      abortController = undefined;
      quantityRulesAbortController = undefined;

      pendingRequestUrl = null;

      productRequestId = 0;
      quantityRequestId = 0;

      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      swapUtilityInitialized = false;

      constructor() {
        super();

        this.quantityInput =
          this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber?.();

        this.onVariantChangeUnsubscriber =
          subscribe(
            PUB_SUB_EVENTS.optionValueSelectionChange,
            this.handleOptionValueChange.bind(this)
          );

        this.initQuantityHandlers();

        this.dispatchEvent(
          new CustomEvent('product-info:loaded', {
            bubbles: true,
          })
        );
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();

        this.abortController?.abort();
        this.quantityRulesAbortController?.abort();

        this.onVariantChangeUnsubscriber =
          undefined;

        this.cartUpdateUnsubscriber =
          undefined;
      }

      addPreProcessCallback(callback) {
        if (typeof callback !== 'function') {
          return;
        }

        this.preProcessHtmlCallbacks.push(
          callback
        );
      }

      initializeProductSwapUtility() {
        /*
         * connectedCallback can run more than once
         * if the component is detached/reinserted.
         *
         * Prevent duplicate processing callbacks.
         */
        if (this.swapUtilityInitialized) {
          return;
        }

        this.swapUtilityInitialized = true;

        this.preProcessHtmlCallbacks.push(
          (html) => {
            html
              ?.querySelectorAll(
                '.scroll-trigger'
              )
              .forEach((element) => {
                element.classList.add(
                  'scroll-trigger--cancel'
                );
              });
          }
        );

        this.postProcessHtmlCallbacks.push(
          () => {
            try {
              window.Shopify?.PaymentButton?.init?.();
            } catch (error) {
              console.warn(
                '[Product Info] Payment button initialization failed:',
                error
              );
            }

            try {
              window.ProductModel?.loadShopifyXR?.();
            } catch (error) {
              console.warn(
                '[Product Info] Shopify XR initialization failed:',
                error
              );
            }
          }
        );
      }

      initQuantityHandlers() {
        this.quantityInput =
          this.querySelector('.quantity__input');

        if (!this.quantityInput) {
          return;
        }

        this.quantityForm =
          this.querySelector(
            '.product-form__quantity'
          );

        if (!this.quantityForm) {
          return;
        }

        this.setQuantityBoundries();

        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber?.();

          this.cartUpdateUnsubscriber =
            subscribe(
              PUB_SUB_EVENTS.cartUpdate,
              this.fetchQuantityRules.bind(this)
            );
        }
      }

      handleOptionValueChange(payload = {}) {
        const data = payload?.data;

        if (!data) {
          return;
        }

        const {
          event,
          target,
          selectedOptionValues,
        } = data;

        if (
          !event?.target ||
          !this.contains(event.target)
        ) {
          return;
        }

        if (!target?.dataset) {
          return;
        }

        const optionValues =
          Array.isArray(selectedOptionValues)
            ? selectedOptionValues
            : [];

        /*
         * Save the currently stable button state.
         *
         * If the Shopify section request fails,
         * we restore this state rather than leaving
         * Add to Cart permanently disabled.
         */
        const previousFormState =
          this.captureProductFormState();

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;

        if (!productUrl) {
          this.restoreProductFormState(
            previousFormState
          );

          return;
        }

        this.pendingRequestUrl =
          productUrl;

        const shouldSwapProduct =
          this.dataset.url !== productUrl;

        const shouldFetchFullPage =
          this.dataset.updateUrl === 'true' &&
          shouldSwapProduct;

        this.renderProductInfo({
          requestUrl:
            this.buildRequestUrlWithParams(
              productUrl,
              optionValues,
              shouldFetchFullPage
            ),

          targetId: target.id,

          previousFormState,

          callback: shouldSwapProduct
            ? this.handleSwapProduct(
                productUrl,
                shouldFetchFullPage
              )
            : this.handleUpdateProductInfo(
                productUrl
              ),
        });
      }

      captureProductFormState() {
        const productForm =
          this.productForm;

        const submitButton =
          productForm?.submitButton ||
          productForm?.querySelector(
            '[type="submit"]'
          );

        const submitButtonText =
          productForm?.submitButtonText ||
          submitButton?.querySelector(
            'span'
          );

        if (!submitButton) {
          return null;
        }

        return {
          disabled:
            submitButton.disabled ||
            submitButton.getAttribute(
              'aria-disabled'
            ) === 'true',

          text:
            submitButtonText?.textContent ||
            null,
        };
      }

      restoreProductFormState(state) {
        if (!state || !this.productForm) {
          return;
        }

        this.productForm.toggleSubmitButton?.(
          state.disabled,
          state.text || undefined
        );
      }

      resetProductFormState() {
        const productForm =
          this.productForm;

        productForm?.toggleSubmitButton?.(
          true
        );

        productForm?.handleErrorMessage?.();
      }

      handleSwapProduct(
        productUrl,
        updateFullPage
      ) {
        return (html) => {
          if (!html) {
            throw new Error(
              'Product swap returned no HTML.'
            );
          }

          this.productModal?.remove();

          const selector =
            updateFullPage
              ? "product-info[id^='MainProduct']"
              : 'product-info';

          const fetchedProductInfo =
            html.querySelector(selector);

          if (!fetchedProductInfo) {
            throw new Error(
              'Updated product information was not found in the Shopify response.'
            );
          }

          const variant =
            this.getSelectedVariant(
              fetchedProductInfo
            );

          this.updateURL(
            productUrl,
            variant?.id
          );

          if (updateFullPage) {
            const newTitle =
              html.querySelector(
                'head title'
              );

            const currentTitle =
              document.querySelector(
                'head title'
              );

            if (
              newTitle &&
              currentTitle
            ) {
              currentTitle.innerHTML =
                newTitle.innerHTML;
            }

            const currentMain =
              document.querySelector('main');

            const newMain =
              html.querySelector('main');

            if (
              !currentMain ||
              !newMain
            ) {
              throw new Error(
                'Product page content was not returned correctly.'
              );
            }

            HTMLUpdateUtility.viewTransition(
              currentMain,
              newMain,
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );

            return;
          }

          HTMLUpdateUtility.viewTransition(
            this,
            fetchedProductInfo,
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        };
      }

      renderProductInfo({
        requestUrl,
        targetId,
        callback,
        previousFormState,
      }) {
        if (
          !requestUrl ||
          typeof callback !== 'function'
        ) {
          this.restoreProductFormState(
            previousFormState
          );

          return;
        }

        /*
         * Cancel previous variant/product request.
         */
        this.abortController?.abort();

        this.abortController =
          new AbortController();

        const controller =
          this.abortController;

        const currentRequestId =
          ++this.productRequestId;

        fetch(requestUrl, {
          method: 'GET',

          signal:
            controller.signal,

          headers: {
            Accept: 'text/html',
          },
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `Product info request failed: ${response.status} ${response.statusText}`
              );
            }

            return response.text();
          })
          .then((responseText) => {
            /*
             * Ignore stale responses.
             */
            if (
              controller.signal.aborted ||
              currentRequestId !==
                this.productRequestId
            ) {
              return null;
            }

            const html =
              new DOMParser().parseFromString(
                responseText,
                'text/html'
              );

            return html;
          })
          .then((html) => {
            if (!html) {
              return;
            }

            if (
              currentRequestId !==
              this.productRequestId
            ) {
              return;
            }

            callback(html);

            this.pendingRequestUrl =
              null;

            /*
             * Focus the option that initiated
             * the request if it still exists.
             *
             * getElementById avoids selector errors
             * from special characters in IDs.
             */
            if (targetId) {
              document
                .getElementById(targetId)
                ?.focus?.();
            }
          })
          .catch((error) => {
            if (
              error?.name === 'AbortError'
            ) {
              return;
            }

            /*
             * An older failed request must not
             * interfere with a newer request.
             */
            if (
              currentRequestId !==
              this.productRequestId
            ) {
              return;
            }

            this.pendingRequestUrl =
              null;

            this.restoreProductFormState(
              previousFormState
            );

            console.error(
              '[Product Info]',
              error
            );
          });
      }

      getSelectedVariant(
        productInfoNode
      ) {
        if (!productInfoNode) {
          return null;
        }

        const selectedVariant =
          productInfoNode.querySelector(
            'variant-selects [data-selected-variant]'
          )?.innerHTML;

        if (!selectedVariant) {
          return null;
        }

        try {
          return JSON.parse(
            selectedVariant
          );
        } catch (error) {
          console.error(
            '[Product Info] Unable to parse selected variant data.',
            error
          );

          return null;
        }
      }

      buildRequestUrlWithParams(
        url,
        optionValues = [],
        shouldFetchFullPage = false
      ) {
        const requestUrl =
          new URL(
            url,
            window.location.origin
          );

        /*
         * Remove an old section_id if the supplied
         * product URL already contains one.
         */
        requestUrl.searchParams.delete(
          'section_id'
        );

        requestUrl.searchParams.delete(
          'option_values'
        );

        if (!shouldFetchFullPage) {
          if (this.sectionId) {
            requestUrl.searchParams.set(
              'section_id',
              this.sectionId
            );
          }
        }

        if (optionValues.length) {
          requestUrl.searchParams.set(
            'option_values',
            optionValues.join(',')
          );
        }

        return requestUrl.toString();
      }

      updateOptionValues(html) {
        const variantSelects =
          html.querySelector(
            'variant-selects'
          );

        if (
          variantSelects &&
          this.variantSelectors
        ) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(
        productUrl
      ) {
        return (html) => {
          const variant =
            this.getSelectedVariant(
              html
            );

          this.pickupAvailability?.update?.(
            variant
          );

          this.updateOptionValues(
            html
          );

          this.updateURL(
            productUrl,
            variant?.id
          );

          this.updateVariantInputs(
            variant?.id
          );

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(
            html,
            variant?.featured_media?.id
          );

          const updateSourceFromDestination =
            (
              id,
              shouldHide = () => false
            ) => {
              const source =
                html.getElementById(
                  `${id}-${this.sectionId}`
                );

              const destination =
                this.querySelector(
                  `#${id}-${this.dataset.section}`
                );

              if (
                !source ||
                !destination
              ) {
                return;
              }

              destination.innerHTML =
                source.innerHTML;

              destination.classList.toggle(
                'hidden',
                shouldHide(source)
              );
            };

          updateSourceFromDestination(
            'price'
          );

          updateSourceFromDestination(
            'Sku',
            ({ classList }) =>
              classList.contains(
                'hidden'
              )
          );

          updateSourceFromDestination(
            'Inventory',
            ({ innerText }) =>
              innerText.trim() === ''
          );

          updateSourceFromDestination(
            'Volume'
          );

          updateSourceFromDestination(
            'Price-Per-Item',
            ({ classList }) =>
              classList.contains(
                'hidden'
              )
          );

          this.updateQuantityRules(
            this.sectionId,
            html
          );

          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove(
            'hidden'
          );

          this.querySelector(
            `#Volume-Note-${this.dataset.section}`
          )?.classList.remove(
            'hidden'
          );

          const submitButton =
            html.getElementById(
              `ProductSubmitButton-${this.sectionId}`
            );

          this.productForm?.toggleSubmitButton?.(
            submitButton?.hasAttribute(
              'disabled'
            ) ?? true,
            window.variantStrings?.soldOut ||
              'Sold out'
          );

          if (
            typeof publish ===
              'function' &&
            typeof PUB_SUB_EVENTS !==
              'undefined' &&
            PUB_SUB_EVENTS.variantChange
          ) {
            publish(
              PUB_SUB_EVENTS.variantChange,
              {
                data: {
                  sectionId:
                    this.sectionId,

                  html,

                  variant,
                },
              }
            );
          }
        };
      }

      updateVariantInputs(
        variantId
      ) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, ` +
            `#product-form-installment-${this.dataset.section}`
        ).forEach(
          (productForm) => {
            const input =
              productForm.querySelector(
                'input[name="id"]'
              );

            if (!input) {
              return;
            }

            input.value =
              variantId ?? '';

            input.dispatchEvent(
              new Event('change', {
                bubbles: true,
              })
            );
          }
        );
      }

      getProductUrl(
        url,
        variantId
      ) {
        const productUrl =
          new URL(
            url,
            window.location.origin
          );

        /*
         * Replace rather than concatenate ?variant=
         * so URLs that already contain query
         * parameters remain valid.
         */
        if (variantId) {
          productUrl.searchParams.set(
            'variant',
            variantId
          );
        } else {
          productUrl.searchParams.delete(
            'variant'
          );
        }

        return productUrl;
      }

      updateURL(
        url,
        variantId
      ) {
        if (!url) {
          return;
        }

        const productUrl =
          this.getProductUrl(
            url,
            variantId
          );

        this.querySelector(
          'share-button'
        )?.updateUrl?.(
          productUrl.toString()
        );

        if (
          this.dataset.updateUrl ===
          'false'
        ) {
          return;
        }

        window.history.replaceState(
          {},
          '',
          `${productUrl.pathname}${productUrl.search}${productUrl.hash}`
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton?.(
          true,
          window.variantStrings
            ?.unavailable ||
            'Unavailable'
        );

        const selectors = [
          'price',
          'Inventory',
          'Sku',
          'Price-Per-Item',
          'Volume-Note',
          'Volume',
          'Quantity-Rules',
        ]
          .map(
            (id) =>
              `#${id}-${this.dataset.section}`
          )
          .join(', ');

        /*
         * Scope to THIS product-info.
         *
         * Prevents a main product from hiding
         * similarly named UI inside another
         * product component.
         */
        this.querySelectorAll(
          selectors
        ).forEach(
          ({ classList }) => {
            classList.add('hidden');
          }
        );
      }

      updateMedia(
        html,
        variantFeaturedMediaId
      ) {
        if (!variantFeaturedMediaId) {
          return;
        }

        const mediaGallerySource =
          this.querySelector(
            'media-gallery ul'
          );

        const mediaGalleryDestination =
          html.querySelector(
            'media-gallery ul'
          );

        const refreshSourceData =
          () => {
            if (
              this.hasAttribute(
                'data-zoom-on-hover'
              ) &&
              typeof enableZoomOnHover ===
                'function'
            ) {
              enableZoomOnHover(2);
            }

            const mediaGallerySourceItems =
              Array.from(
                mediaGallerySource.querySelectorAll(
                  'li[data-media-id]'
                )
              );

            const sourceSet =
              new Set(
                mediaGallerySourceItems.map(
                  (item) =>
                    item.dataset.mediaId
                )
              );

            const sourceMap =
              new Map(
                mediaGallerySourceItems.map(
                  (item, index) => [
                    item.dataset.mediaId,
                    {
                      item,
                      index,
                    },
                  ]
                )
              );

            return [
              mediaGallerySourceItems,
              sourceSet,
              sourceMap,
            ];
          };

        if (
          mediaGallerySource &&
          mediaGalleryDestination
        ) {
          let [
            mediaGallerySourceItems,
            sourceSet,
            sourceMap,
          ] = refreshSourceData();

          const mediaGalleryDestinationItems =
            Array.from(
              mediaGalleryDestination.querySelectorAll(
                'li[data-media-id]'
              )
            );

          const destinationSet =
            new Set(
              mediaGalleryDestinationItems.map(
                ({ dataset }) =>
                  dataset.mediaId
              )
            );

          let shouldRefresh =
            false;

          for (
            let i =
              mediaGalleryDestinationItems.length -
              1;
            i >= 0;
            i--
          ) {
            const destinationItem =
              mediaGalleryDestinationItems[
                i
              ];

            if (
              !sourceSet.has(
                destinationItem.dataset
                  .mediaId
              )
            ) {
              mediaGallerySource.prepend(
                destinationItem
              );

              shouldRefresh =
                true;
            }
          }

          for (
            let i = 0;
            i <
            mediaGallerySourceItems.length;
            i++
          ) {
            if (
              !destinationSet.has(
                mediaGallerySourceItems[i]
                  .dataset.mediaId
              )
            ) {
              mediaGallerySourceItems[
                i
              ].remove();

              shouldRefresh =
                true;
            }
          }

          if (shouldRefresh) {
            [
              mediaGallerySourceItems,
              sourceSet,
              sourceMap,
            ] = refreshSourceData();
          }

          mediaGalleryDestinationItems.forEach(
            (
              destinationItem,
              destinationIndex
            ) => {
              const sourceData =
                sourceMap.get(
                  destinationItem.dataset
                    .mediaId
                );

              if (
                !sourceData ||
                sourceData.index ===
                  destinationIndex
              ) {
                return;
              }

              const referenceNode =
                mediaGallerySource.querySelector(
                  `li:nth-of-type(${
                    destinationIndex +
                    1
                  })`
                );

              if (referenceNode) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  referenceNode
                );
              } else {
                mediaGallerySource.appendChild(
                  sourceData.item
                );
              }

              [
                mediaGallerySourceItems,
                sourceSet,
                sourceMap,
              ] = refreshSourceData();
            }
          );
        }

        this.querySelector(
          'media-gallery'
        )?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        const modalContent =
          this.productModal?.querySelector(
            '.product-media-modal__content'
          );

        const newModalContent =
          html.querySelector(
            'product-modal .product-media-modal__content'
          );

        if (
          modalContent &&
          newModalContent
        ) {
          modalContent.innerHTML =
            newModalContent.innerHTML;
        }
      }

      parseInteger(
        value,
        fallback
      ) {
        const parsed =
          Number.parseInt(
            value,
            10
          );

        return Number.isFinite(parsed)
          ? parsed
          : fallback;
      }

      setQuantityBoundries() {
        if (!this.quantityInput) {
          return;
        }

        const data = {
          cartQuantity:
            this.parseInteger(
              this.quantityInput.dataset
                .cartQuantity,
              0
            ),

          min:
            this.parseInteger(
              this.quantityInput.dataset
                .min,
              1
            ),

          max:
            this.quantityInput.dataset
              .max
              ? this.parseInteger(
                  this.quantityInput.dataset
                    .max,
                  null
                )
              : null,

          step:
            this.parseInteger(
              this.quantityInput.step,
              1
            ),
        };

        let min =
          Math.max(data.min, 0);

        const max =
          data.max === null
            ? null
            : data.max -
              data.cartQuantity;

        if (max !== null) {
          min = Math.min(
            min,
            Math.max(max, 0)
          );
        }

        if (
          data.cartQuantity >=
          data.min
        ) {
          min = Math.min(
            min,
            Math.max(
              data.step,
              1
            )
          );
        }

        this.quantityInput.min =
          min;

        /*
         * Preserve Shopify/Dawn behavior:
         * when there is positive remaining maximum,
         * expose max; otherwise remove it and let
         * server-side inventory validation remain
         * authoritative.
         */
        if (
          max !== null &&
          max > 0
        ) {
          this.quantityInput.max =
            max;
        } else {
          this.quantityInput.removeAttribute(
            'max'
          );
        }

        this.quantityInput.value =
          min;

        if (
          typeof publish ===
            'function' &&
          typeof PUB_SUB_EVENTS !==
            'undefined' &&
          PUB_SUB_EVENTS.quantityUpdate
        ) {
          publish(
            PUB_SUB_EVENTS.quantityUpdate,
            undefined
          );
        }
      }

      fetchQuantityRules() {
        const currentVariantId =
          this.productForm
            ?.variantIdInput
            ?.value;

        if (!currentVariantId) {
          return;
        }

        const spinner =
          this.querySelector(
            '.quantity__rules-cart .loading__spinner'
          );

        spinner?.classList.remove(
          'hidden'
        );

        /*
         * Quantity updates can happen quickly
         * after cart events.
         *
         * Cancel older quantity-rule requests.
         */
        this.quantityRulesAbortController?.abort();

        this.quantityRulesAbortController =
          new AbortController();

        const controller =
          this.quantityRulesAbortController;

        const currentRequestId =
          ++this.quantityRequestId;

        const requestUrl =
          new URL(
            this.dataset.url,
            window.location.origin
          );

        requestUrl.searchParams.set(
          'variant',
          currentVariantId
        );

        requestUrl.searchParams.set(
          'section_id',
          this.dataset.section
        );

        fetch(
          requestUrl.toString(),
          {
            method: 'GET',

            signal:
              controller.signal,

            headers: {
              Accept: 'text/html',
            },
          }
        )
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `Quantity rules request failed: ${response.status} ${response.statusText}`
              );
            }

            return response.text();
          })
          .then((responseText) => {
            if (
              controller.signal.aborted ||
              currentRequestId !==
                this.quantityRequestId
            ) {
              return;
            }

            /*
             * Variant may have changed while
             * this request was downloading.
             */
            if (
              this.productForm
                ?.variantIdInput
                ?.value !==
              currentVariantId
            ) {
              return;
            }

            const html =
              new DOMParser().parseFromString(
                responseText,
                'text/html'
              );

            this.updateQuantityRules(
              this.dataset.section,
              html
            );
          })
          .catch((error) => {
            if (
              error?.name ===
              'AbortError'
            ) {
              return;
            }

            if (
              currentRequestId !==
              this.quantityRequestId
            ) {
              return;
            }

            console.error(
              '[Product Info] Quantity rules:',
              error
            );
          })
          .finally(() => {
            if (
              currentRequestId ===
              this.quantityRequestId
            ) {
              spinner?.classList.add(
                'hidden'
              );
            }
          });
      }

      updateQuantityRules(
        sectionId,
        html
      ) {
        if (
          !this.quantityInput ||
          !this.quantityForm ||
          !sectionId ||
          !html
        ) {
          return;
        }

        const quantityFormUpdated =
          html.getElementById(
            `Quantity-Form-${sectionId}`
          );

        if (!quantityFormUpdated) {
          return;
        }

        const selectors = [
          '.quantity__input',
          '.quantity__rules',
          '.quantity__label',
        ];

        for (
          const selector of selectors
        ) {
          const current =
            this.quantityForm.querySelector(
              selector
            );

          const updated =
            quantityFormUpdated.querySelector(
              selector
            );

          if (
            !current ||
            !updated
          ) {
            continue;
          }

          if (
            selector ===
            '.quantity__input'
          ) {
            const attributes = [
              'data-cart-quantity',
              'data-min',
              'data-max',
              'step',
            ];

            for (
              const attribute of attributes
            ) {
              const valueUpdated =
                updated.getAttribute(
                  attribute
                );

              if (
                valueUpdated !== null
              ) {
                current.setAttribute(
                  attribute,
                  valueUpdated
                );
              } else {
                current.removeAttribute(
                  attribute
                );
              }
            }
          } else {
            current.innerHTML =
              updated.innerHTML;
          }
        }

        this.setQuantityBoundries();
      }

      get productForm() {
        return this.querySelector(
          'product-form'
        );
      }

      get productModal() {
        return document.querySelector(
          `#ProductModal-${this.dataset.section}`
        );
      }

      get pickupAvailability() {
        return this.querySelector(
          'pickup-availability'
        );
      }

      get variantSelectors() {
        return this.querySelector(
          'variant-selects'
        );
      }

      get relatedProducts() {
        if (
          typeof SectionId ===
          'undefined'
        ) {
          return null;
        }

        const relatedProductsSectionId =
          SectionId.getIdForSection(
            SectionId.parseId(
              this.sectionId
            ),
            'related-products'
          );

        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        if (
          typeof SectionId ===
          'undefined'
        ) {
          return null;
        }

        const quickOrderListSectionId =
          SectionId.getIdForSection(
            SectionId.parseId(
              this.sectionId
            ),
            'quick_order_list'
          );

        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return (
          this.dataset.originalSection ||
          this.dataset.section
        );
      }
    }
  );
}